import { forwardRef, useEffect, useState } from 'react';
import { useHierarchicalMenu } from 'react-instantsearch-hooks-web';
import { Disclosure } from '@headlessui/react';
import { oneLine } from 'common-tags';

import ChevronDown from '@/icons/light/chevron-down.svg';
import ChevronUp from '@/icons/light/chevron-up.svg';

import { gtmPush } from '@/utils';
import { getLastRefinedItem } from '../utils';

import type { FunctionComponent, LegacyRef } from 'react';
import type { TypeHierarchicalMenuProps } from './types';

function isModifierClick(event: React.MouseEvent) {
  const isMiddleClick = event.button === 1;

  return Boolean(
    isMiddleClick ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey
  );
}

function HierarchicalList({
  className = '',
  classNames = {
    list: '',
    item: '',
  },
  items,
  createURL,
  onNavigate,
}: any) {
  return (
    <ul className={`${classNames.list} [&_ul_ul_a]:pl-12`}>
      {items.map((item: any) => {
        const Icon = item.isRefined ? ChevronUp : ChevronDown;

        return (
          <li key={item.value}>
            <a
              href={createURL(item.value)}
              className={`flex justify-between gap-4 py-5 pl-3 pr-2 ${
                !item.isRefined
                  ? 'text-text-neutrals-500'
                  : 'text-text-neutrals-900'
              } ${className}`}
              onClick={(event) => {
                if (isModifierClick(event)) {
                  return;
                }
                event.preventDefault();
                onNavigate(item);
              }}
            >
              <div className="flex flex-row items-center gap-1">
                <input
                  type="checkbox"
                  checked={item.isRefined}
                  readOnly
                  className="h-4 w-4 cursor-pointer rounded-[2px] border-neutrals-500 checked:text-primary-500 checked:ring-0 focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <span className="capitalize">{item.label.toLowerCase()}</span>
              </div>

              <div className="flex flex-row items-center gap-1">
                <span>{item.count}</span>
                {item.canRefine && (
                  <Icon className="h-6 w-6" aria-hidden="true" />
                )}
              </div>
            </a>

            {item.data && (
              <HierarchicalList
                items={item.data!}
                onNavigate={onNavigate}
                createURL={createURL}
                className="border-b border-neutrals-50 pl-8 last:border-0"
                classNames={{ list: 'divide-y divide-neutrals-50' }}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * HierarchicalMenu
 */
export const HierarchicalMenu: FunctionComponent<TypeHierarchicalMenuProps> =
  forwardRef(
    (
      {
        className = '',
        classNames = {
          button: '',
          panel: '',
        },
        ...props
      }: TypeHierarchicalMenuProps,
      ref: LegacyRef<HTMLDivElement> | undefined
    ) => {
      const { items, refine, createURL } = useHierarchicalMenu(props);
      const DEFAULT_VALUE = 'Todos los pisos';
      const refinedValue = getLastRefinedItem(items);
      const [zones, setZones] = useState();

      useEffect(() => {
        if (
          refinedValue?.value.split(' > ')?.toString() !==
          (zones || '')?.toString()
        ) {
          setZones(refinedValue?.value.split(' > '));
        }
      }, [refinedValue]);

      useEffect(() => {
        const province = zones?.[0];
        const city = zones?.[1];
        const cityArea = zones?.[2];

        gtmPush({
          event: 'parameter_filter',
          filter_action: refinedValue?.isRefined
            ? 'apply_filter'
            : 'clear_all_filter',
          filter_price_min: undefined,
          filter_price_max: undefined,
          filter_size_min: undefined,
          filter_size_max: undefined,
          filter_property_type: undefined,
          filter_property_condition: undefined,
          filter_province: province,
          filter_city: city,
          filter_city_area: cityArea,
          filter_results_count: refinedValue?.count,
        });
      }, [zones]);

      return (
        <Disclosure>
          {({ open, close }) => {
            const Icon = open ? ChevronUp : ChevronDown;
            return (
              <div className={`text-body text-body--s relative ${className}`}>
                <Disclosure.Button
                  className={oneLine`
                    text-placeholder relative w-full rounded-full border bg-neutrals-0 py-3 pl-4 pr-10
                    ${open ? 'border-neutrals-900' : 'border-neutrals-500'}
                    ${classNames.button}
                  `}
                >
                  <span className="block truncate text-left font-normal">
                    {refinedValue?.value ?? DEFAULT_VALUE}
                  </span>

                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </Disclosure.Button>

                <Disclosure.Panel
                  ref={ref}
                  unmount={false}
                  className={`text-neutral-500 absolute z-10 mt-1 max-h-64 w-full min-w-[250px] overflow-auto overflow-y-scroll rounded-lg border border-neutrals-100 bg-bg-neutrals-0 p-1 shadow-light-s ${classNames.panel}`}
                >
                  <HierarchicalList
                    items={items}
                    onNavigate={(e: any) => {
                      refine(e.value);
                      close();
                    }}
                    createURL={createURL}
                    className="border-b border-neutrals-50 last:border-0"
                    classNames={{ list: 'divide-y divide-neutrals-50' }}
                  />
                </Disclosure.Panel>
              </div>
            );
          }}
        </Disclosure>
      );
    }
  );

HierarchicalMenu.displayName = 'HierarchicalMenu';
